import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Carregando from "./Carregando";
import {
  CHAVE_DE_BUSCA,
  CRITICIDADE,
  FREQUENCIA,
  GATILHO,
  SITUACAO_DA_VARREDURA,
  cnpjNaTela,
  cpfNaTela,
  data,
  dataHora,
  documentoDoSocio,
  frase,
  lerDossie,
  plural,
  urlDoPdf,
  type Achado,
  type AlvoCpf,
  type AlvoEmpresa,
  type Dossie,
  type Resposta,
  type Varredura,
} from "../lib/sentinela";
import { Evidencia, Selo, SeloCriticidade, SeloRevisao, SeloRisco, SeloSeveridade, SeloSituacao } from "./SentinelaUi";

/**
 * O dossiê de um alvo (as telas `/companies/$id` e `/cpfs/$id` de lá), SÓ LEITURA: os apontamentos com a
 * análise da mídia que explica cada um, o quadro de sócios, o cadastro e as varreduras com o PDF. A ordem dos
 * apontamentos, a análise de cada um, o "não encontrado" e se o PDF existe vêm prontos do banco
 * (`sentinela_alvo`, 000397).
 *
 * As ações de lá (rodar varredura, analisar a mídia, gerar o PDF, confirmar ou descartar um apontamento,
 * monitorar um sócio, a aba Configuração) são a fase 3b e ficam fora: a aba Configuração virou o bloco
 * "Monitoramento" do cadastro, só para ler.
 */

type Aba = "apontamentos" | "qsa" | "cadastro" | "varreduras";

export default function SentinelaDossie({ tipo, id, base }: { tipo: "empresa" | "cpf"; id: string; base: string }) {
  const [busca, setBusca] = useSearchParams();
  const [estado, setEstado] = useState<{ chave: string; r: Dossie | Resposta } | null>(null);
  const chave = `${tipo}:${id}`;
  // A leitura guarda o pedido a que responde (a cicatriz da Correção CFC): na troca de alvo, a tela não mostra o
  // dossiê do anterior enquanto o novo não chega.
  const r = estado && estado.chave === chave ? estado.r : null;

  useEffect(() => {
    let vivo = true;
    void lerDossie(tipo, id).then((x) => vivo && setEstado({ chave, r: x }));
    return () => {
      vivo = false;
    };
  }, [tipo, id, chave]);

  const voltar = tipo === "empresa" ? { to: base, texto: "Voltar ao painel" } : { to: `${base}?secao=cpfs`, texto: "Voltar aos CPFs" };

  if (!r) return <Carregando texto="Lendo o dossiê…" />;
  if (!r.ok) {
    return (
      <div className="flex flex-col items-start gap-3" data-dossie-recusado>
        <p className="surface rounded-cf p-5 text-sm text-tinta">{frase(r)}</p>
        <Link to={voltar.to} className="font-semibold text-link underline">
          {voltar.texto}
        </Link>
      </div>
    );
  }

  const abas: { id: Aba; rotulo: string }[] = [
    { id: "apontamentos", rotulo: `Apontamentos (${r.abertos})` },
    ...(tipo === "empresa" ? [{ id: "qsa" as Aba, rotulo: `QSA (${r.socios?.length ?? 0})` }] : []),
    { id: "cadastro", rotulo: "Cadastro" },
    { id: "varreduras", rotulo: `Varreduras (${r.varreduras.length})` },
  ];
  const pedida = busca.get("aba");
  const aba: Aba = abas.find((a) => a.id === pedida)?.id ?? "apontamentos";
  const ir = (a: Aba) => {
    const b = new URLSearchParams(busca);
    if (a === "apontamentos") b.delete("aba");
    else b.set("aba", a);
    setBusca(b);
  };

  const empresa = tipo === "empresa" ? (r.alvo as AlvoEmpresa) : null;
  const cpf = tipo === "cpf" ? (r.alvo as AlvoCpf) : null;
  const titulo = empresa ? empresa.razao_social || "—" : cpf!.nome;

  return (
    <div className="flex flex-col gap-5" data-dossie={tipo} data-dossie-id={id}>
      <Link to={voltar.to} className="self-start text-sm font-semibold text-link underline">
        {voltar.texto}
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-2xl font-bold text-titulo" data-dossie-titulo>
            {titulo}
          </h3>
          <p className="mt-1 text-sm text-cinza">
            {empresa ? `${cnpjNaTela(empresa.cnpj)} · ${empresa.situacao_cadastral || "—"}` : cpfNaTela(cpf!.cpf)}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <SeloRisco valor={r.alvo.nivel_de_risco} />
          <SeloCriticidade valor={r.alvo.criticidade} />
        </div>
      </header>

      {r.escopo === "equipe" && r.dono && (
        <p className="rounded-cf border border-borda bg-chip px-4 py-2 text-sm text-tinta" data-faixa-dono>
          {tipo === "empresa" ? "Terceiro cadastrado" : "Cadastro feito"} por <strong>{r.dono.nome || "aluno sem nome"}</strong>
          {r.dono.email ? ` (${r.dono.email})` : ""}. Você está vendo por ser da equipe.
        </p>
      )}
      {r.alvo.arquivado_em && (
        <p className="rounded-cf border border-borda bg-chip px-4 py-2 text-sm text-tinta" data-faixa-arquivado>
          <strong>
            {tipo === "empresa" ? "Terceiro arquivado" : "Cadastro arquivado"} em {dataHora(r.alvo.arquivado_em)}.
          </strong>{" "}
          O monitoramento automático não agenda mais varreduras para {tipo === "empresa" ? "ele" : "esta pessoa"}, e nada foi
          apagado: apontamentos, varreduras, alertas e PDFs continuam abaixo.
        </p>
      )}
      {r.alto_aberto && (
        <p role="alert" className="rounded-cf border border-erro-borda bg-erro-fundo px-4 py-2 text-sm text-erro-tinta" data-alerta-de-risco>
          <strong>Alerta de risco:</strong>{" "}
          {tipo === "empresa" ? "este terceiro possui" : "esta pessoa possui"} apontamentos de gravidade alta em aberto.
        </p>
      )}

      <nav className="flex flex-wrap gap-2" aria-label="Partes do dossiê">
        {abas.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => ir(a.id)}
            aria-current={aba === a.id ? "page" : undefined}
            className={`min-h-10 rounded-full px-4 text-sm font-semibold transition ${
              aba === a.id ? "bg-acento text-acento-tinta" : "border border-borda text-tinta hover:border-acento"
            }`}
            data-aba-dossie={a.id}
          >
            {a.rotulo}
          </button>
        ))}
      </nav>

      {aba === "apontamentos" ? (
        <Apontamentos achados={r.achados} />
      ) : aba === "qsa" ? (
        <Socios r={r} />
      ) : aba === "cadastro" ? (
        empresa ? <CadastroDaEmpresa e={empresa} /> : <CadastroDoCpf c={cpf!} />
      ) : (
        <Varreduras varreduras={r.varreduras} />
      )}
    </div>
  );
}

function Apontamentos({ achados }: { achados: Achado[] }) {
  if (achados.length === 0) {
    return <p className="surface rounded-cf p-5 text-sm text-cinza">Nenhum apontamento ainda.</p>;
  }
  return (
    <ul className="flex flex-col gap-3" data-apontamentos>
      {achados.map((f) => {
        const revisao = Boolean(f.analise?.requer_revisao_humana) && f.situacao === "open";
        return (
          <li
            key={f.id}
            className={`surface rounded-cf p-4 [overflow-wrap:anywhere] ${f.situacao === "resolved" ? "border-aviso-borda" : ""} ${f.situacao === "dismissed" ? "bg-chip" : ""}`}
            data-apontamento={f.id}
            data-situacao={f.situacao}
            data-severidade={f.severidade}
          >
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="rounded border border-borda px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-cinza" data-fonte>
                {f.fonte}
              </span>
              <SeloSeveridade valor={f.severidade} />
              <SeloSituacao valor={f.situacao} />
              {revisao && <SeloRevisao />}
            </div>
            <h4 className="mt-2 font-semibold text-titulo" data-apontamento-titulo>
              {f.titulo}
            </h4>
            {f.nome_da_entidade && (
              <p className="mt-1 text-xs text-cinza">
                Vinculado a: {f.nome_da_entidade}
                {f.documento_da_entidade ? ` (${f.documento_da_entidade})` : ""}
              </p>
            )}
            {f.descricao && <p className="mt-2 text-sm text-tinta">{f.descricao}</p>}
            {f.motivo_do_descarte && <p className="mt-2 text-sm italic text-cinza">Justificativa: {f.motivo_do_descarte}</p>}
            {f.situacao === "resolved" && (
              <p className="mt-2 text-sm text-tinta">
                Não mais detectado{f.resolvido_em ? ` em ${dataHora(f.resolvido_em)}` : ""}: a fonte deixou de retornar este registro.
              </p>
            )}
            {f.url_de_referencia && (
              <a href={f.url_de_referencia} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-sm font-semibold text-link underline">
                Ver fonte<span className="sr-only"> (abre em outra aba)</span>
              </a>
            )}
            {revisao && (
              <p className="mt-3 rounded-cf border border-alerta-tinta/40 bg-alerta-fundo px-3 py-2 text-sm text-alerta-tinta">
                O motor de atribuição sinalizou este caso para validação humana obrigatória. A decisão final é do analista: conferir a
                fonte primária e a fundamentação abaixo, e então confirmar o apontamento ou descartá-lo como homônimo.
              </p>
            )}
            {f.analise && <Evidencia analise={f.analise} />}
          </li>
        );
      })}
    </ul>
  );
}

function Socios({ r }: { r: Dossie }) {
  const socios = r.socios ?? [];
  if (socios.length === 0) return <p className="surface rounded-cf p-5 text-sm text-cinza">Nenhum sócio identificado no QSA.</p>;
  return (
    <ul className="surface divide-y divide-borda rounded-cf" data-socios>
      {socios.map((s) => (
        <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3" data-socio>
          <div className="min-w-0">
            <div className="font-semibold text-titulo">{s.nome}</div>
            <div className="text-xs text-cinza">
              {documentoDoSocio(s.documento)} · {s.qualificacao || "—"}
            </div>
          </div>
          {s.monitorado ? <Selo tom="acento">Monitorado</Selo> : <Selo tom="neutro">Não monitorado</Selo>}
        </li>
      ))}
    </ul>
  );
}

function Pares({ titulo, linhas }: { titulo: string; linhas: [string, React.ReactNode][] }) {
  return (
    <section className="surface rounded-cf p-4">
      <h4 className="mb-3 font-semibold text-titulo">{titulo}</h4>
      <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
        {linhas.map(([k, v]) => (
          <div key={k}>
            <dt className="text-xs text-cinza">{k}</dt>
            <dd className="text-sm text-tinta">{v === null || v === undefined || v === "" ? "—" : v}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** A próxima varredura só vale com o monitoramento ligado e uma recorrência (a regra da ficha do CPF de lá). */
function proxima(a: { monitorado: boolean; frequencia: string; proxima_varredura_em: string | null }) {
  return a.monitorado && a.frequencia !== "none" && a.proxima_varredura_em ? dataHora(a.proxima_varredura_em) : "—";
}

function Monitoramento({ a, documento }: { a: AlvoEmpresa | AlvoCpf; documento: string }) {
  return (
    <Pares
      titulo="Monitoramento"
      linhas={[
        ["Monitoramento", a.monitorado ? "Ativo" : "Desligado"],
        ["Criticidade", CRITICIDADE[a.criticidade] ?? a.criticidade],
        ["Recorrência", FREQUENCIA[a.frequencia] ?? a.frequencia],
        ["Chave de pesquisa", a.chave_de_busca === "documento" ? documento : (CHAVE_DE_BUSCA[a.chave_de_busca] ?? a.chave_de_busca)],
        ["E-mail para alertas", a.email_de_alerta],
        ["Cadastrado em", data(a.criado_em)],
        ["Última varredura", a.ultima_varredura_em ? dataHora(a.ultima_varredura_em) : "nunca"],
        ["Próxima varredura", proxima(a)],
      ]}
    />
  );
}

function CadastroDaEmpresa({ e }: { e: AlvoEmpresa }) {
  return (
    <div className="flex flex-col gap-4" data-cadastro>
      <Pares
        titulo="Cadastro"
        linhas={[
          ["Razão social", e.razao_social],
          ["Nome fantasia", e.nome_fantasia],
          ["CNPJ", cnpjNaTela(e.cnpj)],
          ["Situação cadastral", e.situacao_cadastral],
          ["CNAE principal", [e.cnae_principal, e.cnae_descricao].filter(Boolean).join(" · ")],
          ["Município / UF", `${e.municipio || "—"} / ${e.uf || "—"}`],
          ["Porte", e.porte],
          ["Data de abertura", data(e.data_abertura)],
        ]}
      />
      <Monitoramento a={e} documento="Documento (CNPJ ou CPF)" />
    </div>
  );
}

function CadastroDoCpf({ c }: { c: AlvoCpf }) {
  return (
    <div className="flex flex-col gap-4" data-cadastro>
      <Pares titulo="Cadastro" linhas={[["Nome", c.nome], ["CPF", cpfNaTela(c.cpf)]]} />
      <Monitoramento a={c} documento="Documento (CPF)" />
      {c.observacoes && (
        <section className="surface rounded-cf p-4">
          <h4 className="mb-2 font-semibold text-titulo">Observações</h4>
          <p className="whitespace-pre-line text-sm text-tinta">{c.observacoes}</p>
        </section>
      )}
    </div>
  );
}

function Varreduras({ varreduras }: { varreduras: Varredura[] }) {
  const [erro, setErro] = useState<string | null>(null);
  if (varreduras.length === 0) return <p className="surface rounded-cf p-5 text-sm text-cinza">Nenhuma varredura realizada.</p>;

  // O link é assinado no CLIQUE, por um minuto, e o arquivo vem como download: navegar até ele não tira ninguém
  // da tela.
  const baixar = async (caminho: string) => {
    setErro(null);
    const url = await urlDoPdf(caminho);
    if (url) window.location.assign(url);
    else setErro("Não foi possível abrir o PDF agora. Confira a conexão e tente de novo.");
  };

  return (
    <div className="flex flex-col gap-3">
      {erro && (
        <p role="alert" className="rounded-cf border border-erro-borda bg-erro-fundo px-4 py-2 text-sm text-erro-tinta">
          {erro}
        </p>
      )}
      <ul className="surface divide-y divide-borda rounded-cf" data-varreduras>
        {varreduras.map((v) => (
          <li key={v.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-varredura={v.id}>
            <div className="min-w-0">
              <div className="font-semibold text-titulo">{dataHora(v.criado_em)}</div>
              <div className="text-xs text-cinza">
                {GATILHO[v.gatilho] ?? v.gatilho} · {SITUACAO_DA_VARREDURA[v.situacao] ?? v.situacao} ·{" "}
                {plural(v.achados, "apontamento", "apontamentos")} ({plural(v.achados_novos, "novo", "novos")})
                {v.fontes_consultadas && v.fontes_consultadas.length > 0 ? ` · Fontes: ${v.fontes_consultadas.join(", ")}` : ""}
              </div>
              {v.mensagem_de_erro && <p className="mt-1 text-xs text-erro-tinta">{v.mensagem_de_erro}</p>}
            </div>
            {v.pdf && (
              <button
                type="button"
                onClick={() => void baixar(v.pdf!)}
                className="min-h-10 rounded-full border border-borda px-4 text-sm font-semibold text-tinta transition hover:border-acento"
                data-baixar-pdf={v.pdf}
              >
                Baixar o PDF
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
